<script setup lang="ts">
import { Transaction } from "../../types/PersologOnlineTypes.ts";
import { useI18n } from "vue-i18n";
import { ref, computed, PropType } from "vue";
import { filenameFromTimeRange, formatDate } from "../../utils/GeneralUtils.ts";
import TooltipButton from "../common/TooltipButton.vue";
import { FilterMatchMode } from "@primevue/core/api";
import { PROFILE_RECLAIM_FLAG, useFeatureFlag } from "../../composables/useFeatureFlag.ts";

const { t } = useI18n();

const props = defineProps({
    transactions: {
        type: Object as PropType<Transaction[]>,
        required: true,
    },
    headline: {
        type: String,
        required: true,
    },
    withIcon: {
        type: Boolean,
        default: false,
    },
    rows: {
        type: Number,
        default: 5,
    },
    tooltipMessage: {
        type: String,
        default: "",
    },
});

const rows = ref(props.rows);
const showPaginator = computed(() => props.transactions.length > 0);
const dt = ref();

// The date column filters and exports on the local-time text the user sees,
// while sorting stays on the raw UTC field for chronological order.
const displayedTransactions = computed(() =>
    props.transactions.map((transaction) => ({
        ...transaction,
        dateDisplay: formatDate(transaction.date, t("formats.datetime")),
    }))
);

const filters = ref({
    productDescription: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.IN },
    quantity: { value: null, matchMode: FilterMatchMode.CONTAINS },
    oldBalance: { value: null, matchMode: FilterMatchMode.CONTAINS },
    newBalance: { value: null, matchMode: FilterMatchMode.CONTAINS },
    dateDisplay: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const filteredTransactions = ref<Transaction[] | null>(null);

function onFilter(event: any) {
    filteredTransactions.value = event.filteredValue || null;
}

const displayedTotalQuantity = computed(() =>
    (filteredTransactions.value ?? props.transactions).reduce(
        (sum, transaction) => sum + transaction.quantity,
        0
    )
);

const exportfilename = computed(() =>
    filenameFromTimeRange(
        props.headline,
        props.transactions[0]?.date,
        props.transactions[props.transactions.length - 1]?.date
    )
);

const exportCSV = () => dt.value.exportCSV();

const escapeCsv = (value: unknown) => String(value ?? "").replace(/"/g, '""');

/**
 * The transaction-type column stores the raw enum (e.g. "USE_PROFILE"), so without a custom
 * export it would be omitted or exported untranslated. Export the localized label shown in the
 * table; every other column keeps its raw value, escaped like PrimeVue's default export.
 */
const exportFunction = (event: { data: unknown; field: string }) => {
    if (event.field === "type") {
        return escapeCsv(t(`header.accountSettings.transactionTable.types.${String(event.data).toLowerCase()}`));
    }
    if (event.field === "date") {
        return escapeCsv(formatDate(String(event.data), t("formats.datetime")));
    }
    return escapeCsv(event.data);
};

const profileReclaimEnabled = useFeatureFlag(PROFILE_RECLAIM_FLAG);

// The reclaim filter entries follow the feature flag; rows the backend already wrote keep
// rendering their label and icon regardless, so existing data stays readable.
const typeOptions = computed(() => [
    { label: t("header.accountSettings.transactionTable.types.purchase_shop"), value: "PURCHASE_SHOP" },
    { label: t("header.accountSettings.transactionTable.types.purchase_monthly_billing"), value: "PURCHASE_MONTHLY_BILLING" },
    { label: t("header.accountSettings.transactionTable.types.refund_profile"), value: "REFUND_PROFILE" },
    { label: t("header.accountSettings.transactionTable.types.use_profile"), value: "USE_PROFILE" },
    { label: t("header.accountSettings.transactionTable.types.support"), value: "SUPPORT" },
    { label: t("header.accountSettings.transactionTable.types.sale_to_sub_account"), value: "SALE_TO_SUB_ACCOUNT" },
    { label: t("header.accountSettings.transactionTable.types.purchase_from_distributor"), value: "PURCHASE_FROM_DISTRIBUTOR" },
    { label: t("header.accountSettings.transactionTable.types.transfer_sent"), value: "TRANSFER_SENT" },
    { label: t("header.accountSettings.transactionTable.types.transfer_received"), value: "TRANSFER_RECEIVED" },
    ...(profileReclaimEnabled.value
        ? [
              { label: t("header.accountSettings.transactionTable.types.retrieval_from_sub_account"), value: "RETRIEVAL_FROM_SUB_ACCOUNT" },
              { label: t("header.accountSettings.transactionTable.types.retrieval_by_distributor"), value: "RETRIEVAL_BY_DISTRIBUTOR" },
          ]
        : []),
]);

// Add this computed or function to the script section
const getIconForType = (type: string): string => {
    switch (type.toLowerCase()) {
        case 'purchase_monthly_billing':
        case 'purchase_shop':
        case 'purchase_from_distributor':
            return 'pi pi-shopping-cart';
        case 'refund_profile':
            return 'pi pi-plus-circle';
        case 'use_profile':
            return 'pi pi-minus-circle';
        case 'sale_to_sub_account':
            return 'pi pi-warehouse';
        case 'transfer_sent':
            return 'pi pi-send';
        case 'transfer_received':
            return 'pi pi-inbox';
        case 'support':
            return 'pi pi-headphones';
        case 'retrieval_from_sub_account':
        case 'retrieval_by_distributor':
            return 'pi pi-history';
        default:
            return 'pi pi-question-circle';
    }
};

</script>

<template>
    <DataTable
        v-model:filters="filters"
        filter-display="menu"
        :value="displayedTransactions"
        ref="dt"
        :id="'transaction-table_data-table'"
        :paginator="showPaginator"
        :rows="rows"
        :style="{ minWidth: '100%' }"
        :export-filename="exportfilename"
        :export-function="exportFunction"
        @filter="onFilter"
    >
        <template #header v-if="headline">
            <div class="flex items-center gap-2">
                <h3 class="m-0">{{ headline }}</h3>
                <TooltipButton
                    v-if="tooltipMessage"
                    :content="tooltipMessage"
                    background-color="white"
                    />
                </div>
        </template>

        <Column
            field="productDescription"
            :header="t('header.accountSettings.transactionTable.product')"
            sortable
            :show-filter-match-modes="false"
        >
            <template #body="slotProps">
                {{ slotProps.data.productDescription }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText
                    v-model="filterModel.value"
                    type="text"
                    @input="filterCallback()"
                    :placeholder="t('fields.search')"
                />
            </template>
        </Column>
        <Column
            field="type"
            :header="t('header.accountSettings.transactionTable.balanceActivityType')"
            sortable
            filter-field="type"
            :show-filter-match-modes="false"
        >
            <template #body="slotProps">
                <i
                    v-if="withIcon"
                    :class="[getIconForType(slotProps.data.type), 'mr-2']"
                />
                <span class="mr-2" v-tooltip="t(`trainer-transactions.transaction-table.tooltips.${slotProps.data.type.toLowerCase()}`)">
                    {{ t(`header.accountSettings.transactionTable.types.${slotProps.data.type.toLowerCase()}`) }}
                </span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                    v-model="filterModel.value"
                    :options="typeOptions"
                    optionLabel="label"
                    optionValue="value"
                    @change="filterCallback()"
                >
                    <template #option="slotProps">
                        <span>{{ slotProps.option.label }}</span>
                    </template>
                </MultiSelect>
            </template>
        </Column>
        <Column field="quantity" :header="t('header.accountSettings.transactionTable.quantity')" sortable :show-filter-match-modes="false">
            <template #filter="{ filterModel, filterCallback }">
                <InputText
                    v-model="filterModel.value"
                    type="text"
                    @input="filterCallback()"
                    :placeholder="t('fields.search')"
                    style="min-width: 12rem"
                    :showClear="true"
                />
            </template>
        </Column>
        <Column field="oldBalance" :header="t('header.accountSettings.transactionTable.oldBalance')" sortable :show-filter-match-modes="false">
            <template #body="slotProps">
                {{ slotProps.data.oldBalance }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText
                    v-model="filterModel.value"
                    type="text"
                    @input="filterCallback()"
                    :placeholder="t('fields.search')"
                />
            </template>
        </Column>
        <Column field="newBalance" :header="t('header.accountSettings.transactionTable.newBalance')" sortable :show-filter-match-modes="false">
            <template #body="slotProps">
                {{ slotProps.data.newBalance }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText
                    v-model="filterModel.value"
                    type="text"
                    @input="filterCallback()"
                    :placeholder="t('fields.search')"
                />
            </template>
        </Column>
        <Column field="date" filter-field="dateDisplay" :header="t('header.accountSettings.transactionTable.date')" sortable :show-filter-match-modes="false">
            <template #body="slotProps">
                {{ slotProps.data.dateDisplay }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText
                    v-model="filterModel.value"
                    type="text"
                    @input="filterCallback()"
                    :placeholder="t('fields.search')"
                />
            </template>
        </Column>

        <template #paginatorstart>
            <strong>{{ t("common.total.quantity") }}:</strong>
            <Tag id="transaction-table_tag_total-quantity" class="ml-2" :value="displayedTotalQuantity" />
        </template>
        <template #paginatorend>
            <Button
                id="transaction-table_data-table_export-button"
                icon="pi pi-download"
                text
                :label="t('buttons.export')"
                size="small"
                @click="exportCSV()"
            />
        </template>
    </DataTable>
</template>
